import React, { FunctionComponent, useState } from 'react';
import { Container, Grid, Header } from 'semantic-ui-react';
import { pathOr, isEmpty, propOr } from 'ramda';
import withStyles from 'react-jss';
import ReactTooltip from 'react-tooltip';
import { FormattedNumber } from 'react-intl';
import moment from 'moment';

import ProgressBar from 'scenes/AccountManagement/components/ProgressBar';
import CFSModal from 'scenes/AccountManagement/components/CFSModal';
import {
  MerchantCustomerProfile,
  AccountDetails,
  PreferredPaymentInstrument,
} from 'scenes/AccountManagement/AccountManagement.helpers';
import { ClassProp } from 'helpers/types';
import { CampaignSummary } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantCampaigns/MerchantCampaigns.helpers';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  merchantCustomerProfile: MerchantCustomerProfile;
  campaigns: CampaignSummary[];
  accountDetails: AccountDetails;
};

const styles = {
  budgetRemaining: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  topGrid: {
    marginTop: '.5rem !important',
    padding: '1.25rem !important',
    boxShadow: '0 .125rem .375rem 0 rgba(0,0,0,0.15) !important',
    marginLeft: '0 !important',
    marginRight: '0 !important',
    background: '#fff !important',
    marginBottom: '2rem !important',
  },
  leftCol: {
    paddingRight: '3.75rem !important',
    borderRight: '0.0625rem solid #e6e7e8',
  },
  rightCol: {
    paddingLeft: '1.875rem !important',
  },
  paymentText: {
    marginBottom: '0.3125rem !important',
    fontSize: '0.875rem !important',
  },
  paymentStrong: {
    fontWeight: '500 !important',
  },
  topHeader: {
    marginTop: '2.5rem !important',
  },
};

const now = new Date();

const findCurrentCampaign = (campaigns: CampaignSummary[]) => {
  return campaigns.find((campaign: any) => {
    const endDate = pathOr({}, ['campaign', 'endDate'], campaign);
    if (moment(endDate).isAfter(now)) {
      return campaign;
    }
  });
};

const currencyOptions = {
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'symbol',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

const renderCFSModal = (
  merchantCustomerProfile: MerchantCustomerProfile,
  accountDetails: AccountDetails,
  buttonText: string,
  setPaymentStatus: Function,
) => (
  <div>
    <CFSModal
      merchantCustomerProfile={merchantCustomerProfile}
      accountDetails={accountDetails}
      buttonText={buttonText}
      setPaymentStatus={setPaymentStatus}
    />
  </div>
);

const renderCampaignDetails = (
  campaigns: CampaignSummary[],
  classes: Styles,
) => {
  if (isEmpty(campaigns)) {
    return <Header as="h2">No Active Campaign Found</Header>;
  }
  const activeCampaign = findCurrentCampaign(campaigns);
  const { campaign, budgets } = activeCampaign as CampaignSummary;

  // Tier3 merchants have a 1 to 1 relationship of campaign to budget
  const { balance, totalBudget } = pathOr({}, [0], budgets);
  return (
    <>
      <h2>Budget Details</h2>
      <p className={classes.paymentText}>
        Campaign Name:&nbsp;
        <span className={classes.paymentStrong}>{campaign.name}</span>
      </p>
      <h4>Budget Remaining</h4>
      <ProgressBar totalBudget={totalBudget} balance={balance} />
      <div className={classes.budgetRemaining}>
        <span>$0</span>
        <FormattedNumber value={balance} {...currencyOptions} />
      </div>
    </>
  );
};

const renderPaymentMethod = (
  merchantCustomerProfile: MerchantCustomerProfile,
  accountDetails: AccountDetails,
  paymentStatus: string | null,
  setPaymentStatus: Function,
  classes: Styles,
) => {
  if (!merchantCustomerProfile) {
    return (
      <div>
        <Header as="h3">Merchant Profile Missing</Header>
      </div>
    );
  }

  const preferredPaymentInstrument = propOr(
    null,
    'preferredPaymentInstrument',
    merchantCustomerProfile,
  ) as PreferredPaymentInstrument;
  if (!preferredPaymentInstrument) {
    return (
      <div>
        <Header as="h3">No Payment Method Found</Header>
        {renderCFSModal(
          merchantCustomerProfile,
          accountDetails,
          'Add Payment Method',
          setPaymentStatus,
        )}
      </div>
    );
  }

  if (paymentStatus === 'pending') {
    return (
      <div>
        <Header as="h3">Payment Method Pending</Header>
      </div>
    );
  }

  const {
    type,
    properties: { accountNickname, last4Digits },
  } = preferredPaymentInstrument;

  return (
    <>
      <h2>Payment Details</h2>
      <p className={classes.paymentText}>
        Payment Type:&nbsp;
        <span className={classes.paymentStrong}>{type}</span>
      </p>
      <p className={classes.paymentText}>
        Account Nickname:&nbsp;
        <span className={classes.paymentStrong}>{accountNickname}</span>
      </p>
      <p className={classes.paymentText}>
        Account Number:&nbsp;
        <span className={classes.paymentStrong}>**** {last4Digits}</span>
      </p>
    </>
  );
};

const CampaignDetails: FunctionComponent<Props> = ({
  classes,
  merchantCustomerProfile,
  campaigns,
  accountDetails,
}) => {
  const [paymentStatus, setPaymentStatus] = useState<string>('');
  return (
    <Container>
      <h3 className={classes.topHeader}>Campaign Details</h3>
      <Grid className={classes.topGrid}>
        <Grid.Row>
          <Grid.Column width={10} className={classes.leftCol}>
            {renderCampaignDetails(campaigns, classes)}
          </Grid.Column>
          <Grid.Column width={6} className={classes.rightCol}>
            {renderPaymentMethod(
              merchantCustomerProfile,
              accountDetails,
              paymentStatus,
              setPaymentStatus,
              classes,
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <ReactTooltip />
    </Container>
  );
};
export default withStyles(styles)(CampaignDetails);
